import React from 'react';

import { formatMoney, toMoney } from '@betterment/js-runtime';
import { Text } from '@betterment/samba-react';

import { ModalSectionRow } from '../../../../Shared/components-v2/modal-section/ModalSectionRow';
import { PurposeSettingsQuery } from '../graphql/purposeSettings.generated';

type Props = {
  target: Extract<PurposeSettingsQuery['purpose']['target'], { __typename: 'TargetRetirementSavings' }>;
};

type MaritalStatus =
  | 'SINGLE'
  | 'HEAD_OF_HOUSEHOLD'
  | 'MARRIED_FILING_JOINTLY'
  | 'MARRIED_FILING_SEPARATELY'
  | 'MARRIED_FILING_SEPARATELY_LIVING_APART'
  | 'QUALIFYING_SURVIVING_SPOUSE';

const taxFilingStatusOptions: Record<MaritalStatus, string> = {
  SINGLE: 'Single',
  HEAD_OF_HOUSEHOLD: 'Head of Household',
  MARRIED_FILING_JOINTLY: 'Married filing jointly',
  MARRIED_FILING_SEPARATELY: 'Married Filing Separately',
  MARRIED_FILING_SEPARATELY_LIVING_APART: 'Married filing separately (living apart)',
  QUALIFYING_SURVIVING_SPOUSE: 'Qualifying surviving spouse',
};

type RowTuple = [string, string | number];

export const TargetRetirementSavings: React.FC<React.PropsWithChildren<Props>> = ({ target }) => {
  const { financialProfile, monthlySpendingAmount } = target;

  const retirementAge = financialProfile.retirementAssumptions?.userAssumptions.retirementAge || 'Not set';
  const spousalRetirementAge = financialProfile.spouse?.retirementUserAssumptions?.retirementAge || null;
  const formattedRetirementAge = spousalRetirementAge
    ? `${retirementAge} / ${spousalRetirementAge} (spouse)`
    : retirementAge;

  const retirementZip = financialProfile.retirementAssumptions?.retirementZip || 'Not set';
  const retirementSpending = monthlySpendingAmount
    ? `${formatMoney(toMoney(monthlySpendingAmount.cents), 'defaultDollar')} monthly`
    : 'Not set';

  const currentIncome = financialProfile.annualPretaxIncome.cents;
  const spousalCurrentIncome = financialProfile.spouse?.annualPretaxIncome?.cents || null;
  const formattedIncome = spousalCurrentIncome
    ? `${formatMoney(toMoney(currentIncome), 'defaultDollar')} / ${formatMoney(
        toMoney(spousalCurrentIncome),
        'defaultDollar'
      )} (spouse)`
    : formatMoney(toMoney(currentIncome), 'defaultDollar');

  const taxStatus = taxFilingStatusOptions[financialProfile.taxFilingStatus as keyof typeof taxFilingStatusOptions];

  const retirementSavingsTuples: RowTuple[] = [
    ['Retirement age', formattedRetirementAge],
    ['Retirement zip', retirementZip],
    ['Desired retirement spending', retirementSpending],
    ['Current income', formattedIncome],
    ['Tax status', taxStatus],
  ];

  return (
    <>
      {retirementSavingsTuples.map(tuple => (
        <ModalSectionRow key={tuple[0]}>
          <ModalSectionRow.LeadingContent>
            <Text>{tuple[0]}</Text>
          </ModalSectionRow.LeadingContent>
          <ModalSectionRow.TrailingContent>
            <Text>{tuple[1]}</Text>
          </ModalSectionRow.TrailingContent>
        </ModalSectionRow>
      ))}
    </>
  );
};
